import * as React from 'react';
import styled from 'styled-components';

import Snackbar from 'crehana-ui/Snackbar';
import Text from 'crehana-ui/Text';
import Dialog from 'crehana-ui/Dialog';
import CUILayout from 'crehana-ui/Layout';

import getLocalStorageStatus, {
  STORAGE_STATUS,
} from 'Jsx/Utils/getLocalStorageStatus';

const List = styled.ol`
  li {
    list-style: decimal;
    margin-left: 16px;
  }
`;

const CookiesDisabledWarning = () => {
  const [isClient, setIsClient] = React.useState(false);
  const [open, setOpen] = React.useState(true);
  const [showExtraInfo, setShowExtraInfo] = React.useState(false);

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  if (!isClient) {
    return null;
  }

  // this code will run only in the client-side thanks to isClient validation
  const localStorageStatus = getLocalStorageStatus();

  if (localStorageStatus === STORAGE_STATUS.AVAILABLE) {
    return null;
  }

  if (showExtraInfo) {
    return (
      <CUILayout>
        <Dialog open={showExtraInfo} onDismiss={() => setShowExtraInfo(false)}>
          <div className="px-16 md:px-40 py-24 pt-40">
            <div>
              <Text as="h3" type="h4">
                Cómo administrar las cookies en mi navegador
              </Text>
              <Text as="p" className="block mt-4">
                Para poder usar todas las funcionalidades de Crehana deberás
                tener las cookies habilitadas. Sigue las instrucciones a
                continuación según tu navegador:
              </Text>
            </div>
            <div className="mt-16">
              <a
                href="https://support.google.com/chrome/answer/95647?co=GENIE.Platform%3DDesktop&hl=es"
                target="_blank"
                rel="noopener noreferrer"
                className="block mb-8"
              >
                <Text type="subtitle1" color="secondary-main">
                  Chrome
                </Text>
              </a>
              <List>
                <li>
                  <Text>Haz clic en Chrome en la barra de menú</Text>
                </li>
                <li>
                  <Text>Haz clic en Preferencias o Configuración</Text>
                </li>
                <li>
                  <Text>
                    Ve hacia el final de la página y haz clic en opciones
                    avanzadas
                  </Text>
                </li>
                <li>
                  <Text>
                    Deslízate hacia abajo buscando la opción Configuración de
                    contenido (Content settings) y haz clic en la pequeña flecha
                    de la derecha de esa opción.
                  </Text>
                </li>
                <li>
                  <Text>
                    Haz clic en la pequeña flecha a un costado de la opción
                    Cookies, allí puedes visualizar todas las opciones para
                    administrar tus cookies.
                  </Text>
                </li>
              </List>
            </div>
            <div className="mt-16">
              <a
                href="https://support.microsoft.com/es-mx/help/17442/windows-internet-explorer-delete-manage-cookies"
                target="_blank"
                rel="noopener noreferrer"
                className="block mb-8"
              >
                <Text type="subtitle1" color="secondary-main">
                  Internet Explorer en adelante
                </Text>
              </a>
              <List>
                <li>
                  <Text>Haz clic en el botón de Herramientas (el engrane)</Text>
                </li>
                <li>
                  <Text>
                    Haz clic en Opciones de Internet y selecciona la pestaña de
                    Privacidad
                  </Text>
                </li>
                <li>
                  <Text>
                    Debajo de configuración haz clic en el botón Avanzada
                  </Text>
                </li>
                <li>
                  <Text>
                    En la ventana emergente Configuración avanzada de
                    privacidad, selecciona la configuración de cookies deseada y
                    haz clic en Aceptar
                  </Text>
                </li>
                <li>
                  <Text>
                    Desde esa ventana tu puedes administrar las cookies de
                    origen y las cookies de terceros, las cuales puedes aceptar,
                    bloquear o que pregunten tu decisión antes de instalarse en
                    tu equipo.
                  </Text>
                </li>
              </List>
            </div>
            <div className="mt-16">
              <a
                href="https://support.mozilla.org/es/kb/los-sitios-web-dicen-que-las-cookies-estan-bloquea"
                target="_blank"
                rel="noopener noreferrer"
                className="block mb-8"
              >
                <Text type="subtitle1" color="secondary-main">
                  Mozilla Firefox – Para PC
                </Text>
              </a>
              <List>
                <li>
                  <Text>Haz clic en Herramientas y después en Opciones.</Text>
                </li>
                <li>
                  <Text>Selecciona Privacidad y Seguridad</Text>
                </li>
                <li>
                  <Text>
                    Verás la opción Cookies y datos del sitio, allí, utiliza las
                    diferentes opciones para administrar tus cookies
                  </Text>
                </li>
              </List>
            </div>
            <div className="mt-16">
              <a
                href="https://support.apple.com/es-es/guide/safari/sfri11471/mac"
                target="_blank"
                rel="noopener noreferrer"
                className="block mb-8"
              >
                <Text type="subtitle1" color="secondary-main">
                  Safari
                </Text>
              </a>
              <List>
                <li>
                  <Text>
                    Dirígete al menú desplegable de Safari y haz clic en
                    Preferencias
                  </Text>
                </li>
                <li>
                  <Text>Haz clic en la opción Privacidad</Text>
                </li>
                <li>
                  <Text>
                    Desde ahí puedes bloquear o desbloquear las cookies o
                    administar las opciones del sitio.
                  </Text>
                </li>
              </List>
            </div>
            <div className="mt-16">
              <a
                href="https://support.microsoft.com/es-mx/help/4468242/microsoft-edge-browsing-data-and-privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="block mb-8"
              >
                <Text type="subtitle1" color="secondary-main">
                  Microsoft Edge
                </Text>
              </a>
              <List>
                <li>
                  <Text>Abre Microsoft Edge</Text>
                </li>
                <li>
                  <Text>
                    Haz clic en el botón configuración y más (puedes buscarlo en
                    la esquina superior como tres puntos) y selecciona
                    Configuración.
                  </Text>
                </li>
                <li>
                  <Text>
                    Deslízate hacia abajo y haz clic en el botón Configuración
                    avanzada.
                  </Text>
                </li>
                <li>
                  <Text>
                    De nuevo deslízate hacia abajo y busca la opción Cookies, y
                    haz clic en el menú desplegable para seleccional la opción
                    que desees.
                  </Text>
                </li>
                <li>
                  <Text>
                    Reinicia Microsoft Edge para que los cambios se hagan
                    efectivos.
                  </Text>
                </li>
              </List>
            </div>
          </div>
        </Dialog>
      </CUILayout>
    );
  }

  return (
    <CUILayout>
      <div>
        <Snackbar
          className="flex flex-col p-40"
          style={{ maxWidth: '490px' }}
          open={open}
          onDismiss={() => {
            setOpen(false);
          }}
          showCloseBtn
        >
          <Text type="subtitle3">Las cookies están inhabilitadas</Text>
          <Text className="block mt-4">
            Las cookies de tu navegador están inhabilitadas.
            <br />
            Debido a esto algunas funcionalidades de Crehana no estarán
            presentes. Asegúrate de que estén habilitadas para navegar sin
            problemas.
          </Text>
          <button
            className="text-left mt-24"
            onClick={() => setShowExtraInfo(true)}
            type="button"
          >
            <Text type="link" color="secondary-main">
              ¿Cómo habilitar las cookies en mi navegador?
            </Text>
          </button>
        </Snackbar>
      </div>
    </CUILayout>
  );
};

export default CookiesDisabledWarning;
